import type { OfflineReview } from './offlineDatabase';
import type { IdMappingEntityType } from './offlineDatabase.offlineEntityStatements';

export interface TempIdMapping {
  tempId: string;
  realId?: number;
  type: IdMappingEntityType;
  createdAt: Date;
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed';
}

export interface OfflineTempIdDatabase {
  getReviews(options: { syncStatus: 'pending' }): Promise<OfflineReview[]>;
  getAllIdMappings(): Promise<Array<{
    tempId: string;
    realId: number;
    entityType: string;
    syncedAt: string;
  }>>;
  saveIdMapping(tempId: string, realId: number, entityType: IdMappingEntityType): Promise<void>;
  getRealIdFromTempId(tempId: string): Promise<number | null>;
}

export class OfflineTempIdStore {
  private mappings: Map<string, TempIdMapping> = new Map();

  constructor(private readonly database: OfflineTempIdDatabase) {}

  generateTempId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  registerPendingMapping(
    tempId: string,
    type: IdMappingEntityType,
    createdAt: Date = new Date()
  ): void {
    this.mappings.set(tempId, {
      tempId,
      type,
      createdAt,
      syncStatus: 'pending',
    });
  }

  async mapTempIdToRealId(
    tempId: string,
    realId: number,
    entityType: IdMappingEntityType = 'review'
  ): Promise<void> {
    const mapping = this.mappings.get(tempId);
    this.mappings.set(tempId, {
      tempId,
      type: entityType,
      createdAt: mapping?.createdAt ?? new Date(),
      realId,
      syncStatus: 'synced',
    });

    await this.database.saveIdMapping(tempId, realId, entityType);
  }

  async getRealId(tempId: string): Promise<number | undefined> {
    const mapping = this.mappings.get(tempId);
    if (mapping?.realId) {
      return mapping.realId;
    }

    const realId = await this.database.getRealIdFromTempId(tempId);
    return realId || undefined;
  }

  async loadMappings(): Promise<void> {
    try {
      const offlineReviews = await this.database.getReviews({ syncStatus: 'pending' });

      for (const review of offlineReviews) {
        if (review.tempId) {
          this.mappings.set(review.tempId, {
            tempId: review.tempId,
            type: 'review',
            createdAt: new Date(review.createdAt),
            syncStatus: review.syncStatus,
          });
        }
      }

      const persistedMappings = await this.database.getAllIdMappings();
      for (const mapping of persistedMappings) {
        if (!this.mappings.has(mapping.tempId)) {
          this.mappings.set(mapping.tempId, {
            tempId: mapping.tempId,
            type: mapping.entityType as IdMappingEntityType,
            createdAt: new Date(mapping.syncedAt),
            syncStatus: 'synced',
            realId: mapping.realId,
          });
        }
      }
    } catch (error) {
      console.warn('[OfflineTempIdStore] Error loading temp ID mappings:', error);
    }
  }

  clear(): void {
    this.mappings.clear();
  }

  get size(): number {
    return this.mappings.size;
  }
}
